import { Checkbox, Table, Tag } from 'antd';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { InputFilter } from 'components/Filters/InputFilter';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { SelectTableFilter } from 'components/Filters/SelectTableFilter';
import { SumRange } from 'components/Filters/SumRange';
import {
  COLUMN_EXTRA_SMALL,
  COLUMN_LARGE,
  COLUMN_MEDIUM,
  COLUMN_PRE_LARGE,
  PAGE_SIZE_OPTIONS,
  SCROLL_Y_WITH_SUMMARY,
} from 'constants/table';
import { FilterOptionsWrapper } from 'ui/Filter';
import { checkValue, formatDate } from 'utils';
import { thousandFormatter } from 'utils/forms/formatter';
import {
  cardPaymentType,
  cardPaymentTypesList,
  paymentStatusToText,
  paymentStatuses,
} from 'utils/handbooks/paymentsHandbook';
import { filterColumns, priceRender, renderWithTooltip } from 'utils/table';

function PayedTaxesTable({
  items,
  loading,
  totalRows,
  current,
  pageSize,
  filters = {},
  summary,
  allowFilter = false,
  onRowClick,
  onChange,
  onFilter,
}) {
  const {
    status,
    f_created,
    t_created,
    f_sum,
    t_sum,
    visa,
    ordernum,
    pay_type,
    desc,
  } = filters;

  const columns = [
    {
      width: COLUMN_LARGE,
      title: 'Клиент',
      key: 'company_name',
      dataIndex: 'company_name',
      // fixed: 'left',
      render: (value) => renderWithTooltip(value, 60),
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Сумма',
      key: 'amount',
      dataIndex: 'amount',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={f_sum}
            toValue={t_sum}
            getRange={(first, second) =>
              onFilter({ f_sum: first, t_sum: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_sum || t_sum} />,
    },
    {
      width: COLUMN_PRE_LARGE,
      title: 'Статус',
      key: 'status',
      dataIndex: 'status',
      render: (status) => {
        return <Tag>{paymentStatusToText[status] || status}</Tag>;
      },
      filterDropdown: () => {
        return (
          <SelectTableFilter
            multiple
            value={status}
            dataSourse={paymentStatuses}
            placeholder="Выберите cтатус"
            getSelectedItem={(status) => onFilter({ status })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={status.length} />,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Дата созд.',
      key: 'create_date',
      dataIndex: 'create_date',
      render: (date) => {
        if (date === checkValue(date)) return formatDate(date, 'DD.MM.YYYY');
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[f_created, t_created]}
            onFilterChange={([start, end]) => {
              onFilter({ f_created: start, t_created: end });
            }}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={f_created && t_created} />,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Скидка (VISA)',
      key: 'visa_amount',
      dataIndex: 'visa_amount',
      render: priceRender,
      filterDropdown: () => {
        return (
          <FilterOptionsWrapper>
            <Checkbox
              checked={visa}
              onChange={(e) => onFilter({ visa: e.target.checked })}
            >
              С VISA
            </Checkbox>
          </FilterOptionsWrapper>
        );
      },
      filterIcon: () => <FilterIcon isActive={visa} />,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Сумма ком',
      key: 'fee',
      dataIndex: 'fee',
      render: priceRender,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Payda ком.',
      key: 'payda_fee',
      dataIndex: 'payda_fee',
      render: priceRender,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Экв. ком.',
      key: 'acquiring_fee',
      dataIndex: 'acquiring_fee',
      render: priceRender,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Транзит ком.',
      key: 'kgd_fee',
      dataIndex: 'kgd_fee',
      render: priceRender,
    },
    {
      width: COLUMN_MEDIUM,
      title: '№',
      key: 'order',
      dataIndex: 'order',
      filterDropdown: () => {
        return (
          <InputFilter
            value={ordernum}
            placeholder="Поиск по номеру заказа"
            onFilterChange={(ordernum) => onFilter({ ordernum })}
          />
        );
      },
      filterIcon: () => <FilterIcon search isActive={ordernum} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Тип',
      key: 'type',
      dataIndex: 'type',
      render: (value) => checkValue(cardPaymentType[value]),
      filterDropdown: () => {
        return (
          <SelectTableFilter
            value={pay_type}
            dataSourse={cardPaymentTypesList}
            placeholder="Выберите тип"
            getSelectedItem={(pay_type) => onFilter({ pay_type })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={pay_type} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Описание',
      key: 'desc',
      dataIndex: 'desc',
      render: (value) => renderWithTooltip(value),
      filterDropdown: () => {
        return (
          <InputFilter
            value={desc}
            placeholder="Поиск по описанию"
            onFilterChange={(desc) => onFilter({ desc })}
          />
        );
      },
      filterIcon: () => <FilterIcon search isActive={desc} />,
    },
  ];

  const renderSummary = () => {
    const {
      total_amount,
      total_fee,
      total_payda_fee,
      total_visa_amount,
      total_acquiring_fee,
      total_kgd_fee,
    } = summary;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ color: 'rgba(0,0,0,0.5)' }}>
          <Table.Summary.Cell>Итого</Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_amount)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_visa_amount)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_fee)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_payda_fee)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_acquiring_fee)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_kgd_fee)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Table
      columns={filterColumns(columns, allowFilter)}
      dataSource={items}
      loading={loading}
      summary={summary ? renderSummary : null}
      scroll={{ x: 1400, y: SCROLL_Y_WITH_SUMMARY }}
      rowClassName={(record) =>
        record?.priority === 1 ? 'table-row-danger' : ''
      }
      pagination={{
        total: totalRows,
        current,
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      rowKey="id"
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }

        return null;
      }}
      onChange={onChange}
    />
  );
}

export { PayedTaxesTable };
